import React from "react"
import styled, { css } from "styled-components"
import colors from "@constants/colors"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import { Close } from "@components/icons"
import EntryType from "./EntryType"
import ProductSeries from "./ProductSeries"

export const EXPLORE_MENU_LAPTOP_WIDTH = 530
export const EXPLORE_MENU_DESKTOP_WIDTH = 672

const Container = styled.div<{ topOffset?: number }>`
  position: absolute;
  align-items: flex-start;
  justify-content: center;
  display: none;
  overflow: hidden;
  overflow-y: auto;
  width: 100vw;
  ${({ topOffset }) => `max-height: calc(100vh - ${topOffset || 88}px - 80px);`}
  background-color: ${colors.offWhite};
  cursor: default;

  @media ${breakpoints.laptop} {
    display: flex;
  }
`

const Content = styled.div<{ exploreEntryTypes: number }>`
  ${({ exploreEntryTypes }) => css`
    flex-direction: column;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    display: flex;
    width: ${exploreEntryTypes > 2
      ? `calc(${exploreEntryTypes} * 200px)`
      : `${EXPLORE_MENU_LAPTOP_WIDTH}px`};
    padding: 100px 0 92px;

    @media ${breakpoints.desktop} {
      width: ${EXPLORE_MENU_DESKTOP_WIDTH}px;
    }
  `}
`

const EntryTypes = styled.div`
  display: flex;
  margin-bottom: 116px;
`

const SideMenu = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  flex-direction: column;
  align-items: flex-end;
  display: flex;
  padding-right: calc(0.5 / 12 * 100vw);
`

const CloseButton = styled.button`
  display: flex;
  transform: background-color ${animation.medium} ${cubicBezier};
  width: 44px;
  height: 44px;
  margin-top: 100px;
  padding: 6px;
  outline: none;
  border: none;
  border-radius: 2px;
  background: ${colors.dividerLineGray};

  &:active {
    outline: none;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    transform: background-color ${animation.quick} ${cubicBezier};
    background-color: ${colors.lightGray};
    cursor: pointer;

    svg {
      stroke: ${colors.offBlack};
    }
  }
`

interface ExploreMenuProps {
  topOffset?: number
  exploreEntryTypes: Array<{
    link: string
    name: string
    description: string
  }>
  productSeriesHeader: string
  productSeries: Array<{
    link: string
    name: string
    color?: string
    icon?: string
    image: string
    onClick?: () => void
  }>
  onRequestClose?: () => void
}

const ExploreMenu: React.FC<ExploreMenuProps> = ({
  topOffset,
  exploreEntryTypes,
  productSeriesHeader,
  productSeries,
  onRequestClose,
}) => {
  return (
    <Container topOffset={topOffset}>
      <Content exploreEntryTypes={exploreEntryTypes.length}>
        <EntryTypes>
          {exploreEntryTypes.map((entryType) => (
            <EntryType
              key={entryType.link}
              width={`calc(100% / ${exploreEntryTypes.length})`}
              {...entryType}
            />
          ))}
        </EntryTypes>
        <ProductSeries
          productSeriesHeader={productSeriesHeader}
          productSeries={productSeries}
        />
      </Content>
      <SideMenu>
        <CloseButton onClick={onRequestClose}>
          <Close strokeColor={colors.mediumDarkGray} strokeWidth="2.5" />
        </CloseButton>
      </SideMenu>
    </Container>
  )
}

export default ExploreMenu
